/**
 * Pure derivations for the Cohorts tab (COHORT-SCHEDULING-PLAN Phase 4).
 *
 * The module stores lifecycle only: a cohort assignment (cohort ×
 * orientation pairing) is `Active` or `Cancelled` — locked decisions 5
 * and 6 killed every scheduling word, so nothing here says "scheduled".
 * The states a coordinator actually watches (Not started / In progress /
 * Complete) are derived client-side from the linked `assignment` rows,
 * which reach the portal through `cohort_progress` carrying
 * `cohort_assignment_id`.
 *
 * Per-member "why is this member not assigned" is also derived, not
 * stored (the plan dropped #127's `assign_error` column): the reducer
 * skip reasons are reconstructable from the member's OTHER assignment
 * rows for the same orientation.
 */

export type LinkedAssignmentStatus =
  | 'NotStarted'
  | 'InProgress'
  | 'Completed'
  | 'Cancelled';

export type StoredPairingStatus = 'Active' | 'Cancelled';

export type PairingDisplayStatus =
  | 'Not started'
  | 'In progress'
  | 'Complete'
  | 'Cancelled';

/**
 * Roll the linked assignments up into the pairing's display status.
 *
 * - Cancelled pairing → 'Cancelled', regardless of assignments (started
 *   members keep running server-side; the row-level detail shows that).
 * - Individually-cancelled member assignments are excluded from the
 *   rollup — three of five finishing still completes the pairing (the
 *   #127 edge, kept: a cancelled pair must not pin 'In progress').
 * - All live assignments Completed (and at least one) → 'Complete'.
 * - Any member started or finished → 'In progress'.
 * - Otherwise → 'Not started' (rows exist, nobody has begun; also the
 *   degenerate everyone-individually-cancelled case).
 */
export function pairingDisplayStatus(
  stored: StoredPairingStatus,
  linked: LinkedAssignmentStatus[],
): PairingDisplayStatus {
  if (stored === 'Cancelled') return 'Cancelled';
  const live = linked.filter((s) => s !== 'Cancelled');
  if (live.length > 0 && live.every((s) => s === 'Completed')) return 'Complete';
  if (live.some((s) => s === 'InProgress' || s === 'Completed'))
    return 'In progress';
  return 'Not started';
}

/** One member's relationship to one pairing, as the detail row shows it. */
export type MemberPairingState =
  | { kind: 'assigned'; status: LinkedAssignmentStatus; assignmentId: bigint }
  | { kind: 'not-assigned'; reason: string };

/** The slice of an assignment row the derivations need. */
export type AssignmentSlice = {
  assignmentId: bigint;
  email: string;
  orientationId: string;
  cohortAssignmentId: bigint | null | undefined;
  status: LinkedAssignmentStatus;
};

/**
 * Why `assign_cohort` (or the back-fill) skipped this member, derived
 * from their other rows for the same orientation — mirrors the module's
 * `member_assign_action`: a live NotStarted row would have been ADOPTED,
 * so a not-assigned member with a live row was skipped because that row
 * is InProgress or Completed (send-back is the retake path, locked
 * decision 11), or it is a NotStarted row that appeared after the pairing
 * was made (e.g. an individual row sent back). No live row at all means the
 * batch write itself failed; the module log carries the real reason.
 */
export function memberPairingState(
  memberEmail: string,
  pairing: { cohortAssignmentId: bigint; orientationId: string },
  assignments: AssignmentSlice[],
): MemberPairingState {
  const email = memberEmail.trim().toLowerCase();
  const mine = assignments.filter((a) => a.email.trim().toLowerCase() === email);

  const linked = mine.find(
    (a) => a.cohortAssignmentId === pairing.cohortAssignmentId,
  );
  if (linked) {
    return {
      kind: 'assigned',
      status: linked.status,
      assignmentId: linked.assignmentId,
    };
  }

  const sameOrientation = mine.filter(
    (a) => a.orientationId === pairing.orientationId && a.status !== 'Cancelled',
  );
  // Send-back only takes a Completed row (and lives in the Review Queue), so
  // it is no advice for a run in progress: that run just carries on outside
  // this pairing.
  if (sameOrientation.some((a) => a.status === 'InProgress')) {
    return {
      kind: 'not-assigned',
      reason: 'already mid-orientation individually — they finish that run outside this pairing',
    };
  }
  if (sameOrientation.some((a) => a.status === 'Completed')) {
    return {
      kind: 'not-assigned',
      reason:
        'already completed this orientation individually — a retake (Send back in the Review Queue) stays outside this pairing',
    };
  }
  // A live NotStarted row that is NOT this pairing's: adoption only happens
  // when the pairing is made or the member is added, so e.g. a later
  // send-back of an individual row leaves one sitting outside it. Nothing
  // failed, so the module-log fallback below would be a false alarm.
  if (sameOrientation.some((a) => a.status === 'NotStarted')) {
    return {
      kind: 'not-assigned',
      reason: 'has a separate not-started assignment for this orientation — it runs outside this pairing',
    };
  }
  return {
    kind: 'not-assigned',
    reason: 'no assignment was created — the module log has the reason',
  };
}

/**
 * Why a member ASSIGNED to this pairing can't be put into a new group run
 * (PairingDetail's Regroup picker renders them disabled with this on hover),
 * or `undefined` when they can. Mirrors what `create_group_run` /
 * `set_group_run_members` accept: a NotStarted row stamped with this pairing
 * that no run of it already holds.
 *
 * Completed is checked BEFORE group membership on purpose (#190): a finished
 * run still lists everyone who ran in it, and the way back into a group is
 * Send back in the Review Queue, which moves a group member out of that room
 * and leaves a NotStarted row this pairing can regroup.
 */
export function regroupBlockReason(args: {
  email: string;
  orientationName: string;
  status: LinkedAssignmentStatus;
  inGroupOfThisPairing: boolean;
}): string | undefined {
  const { email, orientationName, status, inGroupOfThisPairing } = args;
  if (status === 'Completed') {
    return `${email} has already completed orientation "${orientationName}" — send them back from the Review Queue, then regroup them here.`;
  }
  if (inGroupOfThisPairing) return `${email} is already in a group of this pairing.`;
  if (status === 'InProgress') {
    // Send-back refuses a run in progress, and Unassign + a fresh assign on
    // the Orientations page yields an INDIVIDUAL row outside this pairing
    // (assign_orientation passes no cohort assignment), so the only way in
    // is finish -> send back -> regroup.
    return `${email} is part-way through orientation "${orientationName}" — once they finish, send them back from the Review Queue, then regroup them here.`;
  }
  if (status === 'Cancelled') {
    return `${email}'s assignment here was cancelled — re-assign them before grouping.`;
  }
  return undefined;
}

/**
 * What is actually UNDER WAY on a cohort right now (#191): live group runs
 * plus people mid-orientation outside one. Drives the three-way archive
 * dialog — shown only when this is non-zero, so the plain case keeps the
 * one-line confirm.
 *
 * A member of a live run is NOT counted again as a solo runner (they are
 * already represented by their run), but a member of a cancelled run is:
 * that person is back on their own. Completed work is never counted —
 * archiving never touches it, and neither does either reducer.
 */
export function liveCohortWork(params: {
  pairings: readonly { cohortAssignmentId: bigint; storedStatus: StoredPairingStatus }[];
  assignments: readonly AssignmentSlice[];
  runs: readonly {
    cohortAssignmentId: bigint;
    statusTag: string;
    members: readonly { assignmentId: bigint }[];
  }[];
}): { liveRuns: number; soloInProgress: number; total: number } {
  const activePairings = new Set(
    params.pairings
      .filter((p) => p.storedStatus === 'Active')
      .map((p) => String(p.cohortAssignmentId)),
  );
  const live = params.runs.filter(
    (r) =>
      activePairings.has(String(r.cohortAssignmentId)) &&
      (r.statusTag === 'Running' || r.statusTag === 'Paused'),
  );
  const inLiveRun = new Set(
    live.flatMap((r) => r.members.map((m) => String(m.assignmentId))),
  );
  const soloInProgress = params.assignments.filter(
    (a) =>
      a.status === 'InProgress' &&
      a.cohortAssignmentId != null &&
      activePairings.has(String(a.cohortAssignmentId)) &&
      !inLiveRun.has(String(a.assignmentId)),
  ).length;
  return { liveRuns: live.length, soloInProgress, total: live.length + soloInProgress };
}
